import {wsRouteByPmTypeMap} from '@/api/configApi';
import {PModuleType} from '@/enums/configs';
import authModule from '@/store/modules/authModule';

export function addSignalSubscription(
  socket: WebSocket,
  params: {
    moduleType: PModuleType;
    moduleGUID: string;
    alias: string;
    eventType: string;
  },
) {
  if (socket) {
    const request = (operation: 'add' | 'drop') => ({
      'type': operation,
      'module_type': wsRouteByPmTypeMap.get(params.moduleType),
      'module_identifier': params.moduleGUID,
      'module_alias': params.alias,
      'event_type': params.eventType,
      'data_type': 'signal',
    });
    socket.send(JSON.stringify(request('add')));
    const dropRequest = JSON.stringify(request('drop'));
    // Возвращаем функцию для отписки
    return () => socket.send(dropRequest);
  }
}

export function addScpectrSubscription(
  socket: WebSocket,
  params: {
    moduleType: PModuleType;
    moduleGUID: string;
    eventType: string;
    spectrumType: number;
  },
) {
  if (socket) {
    const request = (operation: 'add' | 'drop') => ({
      'type': operation,
      'module_type': wsRouteByPmTypeMap.get(params.moduleType),
      'module_identifier': params.moduleGUID,
      'event_type': params.eventType,
      'spectrum_type': params.spectrumType,
      'data_type': 'spectr',
    });
    socket.send(JSON.stringify(request('add')));
    // Возвращаем функцию для отписки
    return () => socket.send(JSON.stringify(request('drop')));
  }
}

export function addRawSignalSubscription(
  socket: WebSocket,
  params: {
    moduleType: PModuleType;
    moduleGUID: string;
    eventType: string;
  },
) {
  if (socket) {
    const request = (operation: 'add' | 'drop') => ({
      'type': operation,
      'module_type': wsRouteByPmTypeMap.get(params.moduleType),
      'module_identifier': params.moduleGUID,
      'event_type': params.eventType,
      'data_type': 'source_signal',
    });
    socket.send(JSON.stringify(request('add')));
    // Возвращаем функцию для отписки
    return () => socket.send(JSON.stringify(request('drop')));
  }
}

export function addAngleSubscription(
  socket: WebSocket,
  params: {
    moduleType: PModuleType;
    moduleGUID: string;
    alias: string;
    eventType: string;
  },
) {
  if (socket) {
    const request = (operation: 'add' | 'drop') => ({
      'type': operation,
      'module_type': wsRouteByPmTypeMap.get(params.moduleType),
      'module_identifier': params.moduleGUID,
      'module_alias': params.alias,
      'event_type': params.eventType,
      'data_type': 'signal',
    });
    socket.send(JSON.stringify(request('add')));
    // Возвращаем функцию для отписки
    return () => socket.send(JSON.stringify(request('drop')));
  }
}

const SERVER_PING_INTERVAL = 30000;

export class SocketManager {
  private socket!: WebSocket;
  private createSocketFunc: () => WebSocket;
  private onMessageList: {[id: string]: (e: any) => void} = {};
  private onRemoveList: {[id: string]: () => void} = {};
  private unsubscriptionList: {[name: string]: () => void} = {};
  private subscriptionList: {[name: string]: (socket: WebSocket) => (() => void) | undefined} = {};
  private listenersCounter: {[name: string]: number} = {};

  manualPongIntervalId!: number;

  constructor(createSocketFunc: () => WebSocket) {
    this.createSocketFunc = createSocketFunc;
    this.connect();
  }

  get messageHandlers() {
    return Object.values(this.onMessageList);
  }

  get subscriptions() {
    return Object.values(this.subscriptionList);
  }

  isSocketOpen() {
    return this.socket && this.socket.readyState === this.socket.OPEN;
  }

  // используем id слушателя и имя подписки чтобы различать подписки на один
  // и тот же слой данных и не посылать повторные подписки и отписки сокету
  addSubscription(
    id: string,
    name: string,
    addSubscriptionFunction: (socket: WebSocket) => (() => void) | undefined,
    handler: (e: any) => void,
  ) {
    if (!this.isSocketOpen() || this.hasSubscription(id)) {
      return;
    }

    this.onMessageList[id] = handler;
    // подписываемся только если ещё не подписывались и сохраняем функцию отписки
    if (!this.listenersCounter[name]) {
      this.listenersCounter[name] = 1;
      this.subscriptionList[name] = addSubscriptionFunction; // Можно перевызвать подписки при переподключении
      const unsubscribe = addSubscriptionFunction(this.socket);
      if (unsubscribe) this.unsubscriptionList[name] = unsubscribe;
    } else {
      this.listenersCounter[name] = this.listenersCounter[name] + 1;
    }

    this.onRemoveList[id] = () => {
      this.listenersCounter[name] = this.listenersCounter[name] - 1;

      // отписываемся только если больше нет слушателей
      if (!this.listenersCounter[name] && this.unsubscriptionList[name]) {
        this.unsubscriptionList[name]();
        delete this.subscriptionList[name];
        delete this.unsubscriptionList[name];
      }
    };
  }

  hasSubscription(id: string) {
    return !!this.onMessageList[id];
  }

  closeConnection() {
    this.socket.close();
  }

  connect() {
    clearInterval(this.manualPongIntervalId);
    this.socket = this.createSocketFunc();
    const accessToken = authModule.getAccessToken || '';
    if (!accessToken) return;

    this.socket.onopen = () => {
      this.socket.onmessage = e => {
        this.messageHandlers.forEach(messageHandler => messageHandler(e));
      };
      // Простое решение проьблемы 1006: пингуем сокет
      this.manualPongIntervalId = setInterval(
        () => this.socket.send(JSON.stringify('ping')),
        // умножаем на 0.6-0.9, чтобы отправлять pong немного чаще
        SERVER_PING_INTERVAL * 0.7,
      );
      // переподписываемся на данные если есть на что
      this.subscriptions.forEach(subscribe => subscribe(this.socket));
    };

    this.socket.onclose = event => {
      clearInterval(this.manualPongIntervalId);
      console.log('Websocket closed', event);
      // переподключаемся если было анормальное закрытие
      if (event.code != 1008) {
        const that = this;
        setTimeout(function() {
          that.connect();
        }, 1000);
      }
    };
    this.socket.onerror = error => {
      console.log('Websocket error: ', error);
    };
  }

  removeSubscription(id: string) {
    if (this.onRemoveList[id] && this.socket.readyState === this.socket.OPEN) {
      // вызваем отписку из onRemoveList только если нет больше слушателей
      this.onRemoveList[id]();
      delete this.onRemoveList[id];
      // хендлер удаляем в любом случае
      delete this.onMessageList[id];
    }
  }

  destroy() {
    if (this.socket) {
      this.socket.close();
    }
    this.onMessageList = {};
    this.onRemoveList = {};
    this.unsubscriptionList = {};
    this.listenersCounter = {};
  }
}
